import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from './Modal';
import Button from './Button';
import Badge from './Badge';
import { Icon } from './Icon';
import { fetchParameters } from '../../store/slices/parametersSlice';

/**
 * SystemConstantPicker — gorev filtresi/exclude degerinde sistem sabiti sec.
 *
 * Admin filter satirinda "Sistem Sabitinden Sec" butonuna tiklar, bu modal
 * acilir. Liste o an yuklenmis Sistem Sabitleri (state.parameters.items).
 * Sectiginde ``${PARAM_KEY}`` formatinda bir string onClose ile parent'a
 * doner. Parent bu stringi filter.value alanina yazar.
 *
 * Boylece admin elle ``${HYO_HARIC_BRANSLAR}`` yazmak zorunda kalmaz —
 * hatali yazim + yanlis key riski ortadan kalkar.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onPick: (paramRefString) => void   // ornek: "${HYO_HARIC_BRANSLAR}"
 *   categoryFilter: 'FILTRE' | 'REFERANS' | null (opsiyonel)
 */
export default function SystemConstantPicker({ open, onClose, onPick, categoryFilter }) {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.parameters.items);
  const status = useSelector((s) => s.parameters.status);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    // Her acilisinda taze data cek — pagination disi full liste.
    const params = { page: 1, size: 200 };
    if (categoryFilter) params.category = categoryFilter;
    dispatch(fetchParameters(params));
  }, [open, dispatch, categoryFilter]);

  const filtered = (items || []).filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.param_key.toLowerCase().includes(q) ||
      (p.label || '').toLowerCase().includes(q)
    );
  });

  const valuePreview = (p) => {
    const v = p.param_value;
    if (Array.isArray(v)) return v.slice(0, 3).join(', ') + (v.length > 3 ? ', ...' : '');
    if (typeof v === 'boolean') return v ? 'Evet' : 'Hayır';
    return String(v);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sistem Sabitinden Seç"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          İptal
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-neutral-600">
          Seçtiğin sabit, filtre değerine <code className="bg-neutral-100 px-1 rounded text-[11px]">${'${KEY}'}</code>{' '}
          referansı olarak yazılır. Sabitin değeri değişince tüm görevler
          otomatik güncellenir. Yeni sabit oluşturmak için{' '}
          <strong>Sistem Sabitleri</strong> ekranını kullan.
        </p>

        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sabit adı veya etiket ara..."
          className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        />

        {status === 'loading' && (
          <p className="text-sm text-neutral-500">Yükleniyor...</p>
        )}

        {status !== 'loading' && filtered.length === 0 && (
          <p className="text-sm text-neutral-500 text-center py-6">
            {search ? 'Eşleşen sabit bulunamadı.' : 'Henüz sistem sabiti yok.'}
          </p>
        )}

        <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 max-h-[50vh] overflow-y-auto">
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onPick(`$\{${p.param_key}\}`);
                onClose?.();
              }}
              className="w-full text-left px-3 py-2.5 hover:bg-primary-50/40 transition-colors focus:outline-none focus:bg-primary-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-xs font-semibold text-primary-700">
                      {p.param_key}
                    </code>
                    {p.is_system && (
                      <Badge color="neutral" size="sm">
                        <Icon name="settings" className="h-3 w-3 mr-0.5" />
                        Sistem
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-neutral-700 mt-0.5">{p.label}</p>
                  <p className="text-[11px] text-neutral-500 mt-0.5 font-mono truncate">
                    = {valuePreview(p)}
                  </p>
                </div>
                <Icon name="plus" className="h-4 w-4 text-neutral-300 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
